/**
 * NotificationContext - Provides toast and modal capabilities to the app
 * Integrates NotificationService with React for modal rendering
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  NotificationService,
  type ToastType,
  type ToastOptionsConfig,
  type ModalConfig,
} from '../services/notification.service';
import { GenericModal } from '../components/common/GenericModal';

export interface NotificationContextType {
  showToast: (message: string, type?: ToastType, options?: ToastOptionsConfig) => void;
  showModal: (config: ModalConfig) => Promise<string>;
  showConfirmation: (
    message: string,
    title?: string,
    options?: { confirmLabel?: string; cancelLabel?: string; danger?: boolean }
  ) => Promise<boolean>;
  dismissToast: (toastId?: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [modalConfig, setModalConfig] = useState<ModalConfig | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = NotificationService.onModalRequest((config) => {
      setModalConfig(config);
      setModalOpen(true);
    });
    return unsubscribe;
  }, []);

  const handleModalAction = useCallback((value: string) => {
    setModalOpen(false);
    NotificationService.resolveModal(value);
    setModalConfig(null);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    NotificationService.resolveModal('cancel');
    setModalConfig(null);
  }, []);

  const showToast = useCallback(
    (message: string, type: ToastType = 'info', options?: ToastOptionsConfig) => {
      NotificationService.showToast(message, type, options);
    },
    []
  );

  const showModal = useCallback((config: ModalConfig) => {
    return NotificationService.showModal(config);
  }, []);

  const showConfirmation = useCallback(
    (
      message: string,
      title?: string,
      options?: { confirmLabel?: string; cancelLabel?: string; danger?: boolean }
    ) => {
      return NotificationService.showConfirmation(message, title, options);
    },
    []
  );

  const dismissToast = useCallback((toastId?: string) => {
    NotificationService.dismissToast(toastId);
  }, []);

  const value: NotificationContextType = {
    showToast,
    showModal,
    showConfirmation,
    dismissToast,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <GenericModal
        open={modalOpen}
        config={modalConfig}
        onAction={handleModalAction}
        onClose={handleModalClose}
      />
    </NotificationContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
